/**
 * EmojiPicker — a small, accessible compose-emoji popover (P3 rework 2026-07-09).
 *
 * There is NO compose emoji affordance in the app today — only message
 * *reactions* (teaminbox Thread onReact). This adds one, shared by the WhatsApp
 * composer (reused Team-Inbox <Composer>) and the Person-lens email composer.
 *
 * Deliberately dependency-free: a curated grid of common emoji (no 4000-glyph
 * data set, no remote font). Tokens only. A11y (Vera gate 2026-07-09):
 *   • role="group" + aria-label — the grid is plain Tab-navigable native
 *     buttons (no arrow-key roving), so it must NOT claim menu semantics.
 *   • Escape / outside-click dismiss RETURNS focus to the trigger.
 *   • Right-edge collision: the popover flips to right-aligned when the trigger
 *     sits near the viewport edge, so it never induces horizontal scroll.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './EmojiPicker.css';

/** Curated set — the everyday glyphs a Vastu practice actually reaches for. */
const EMOJI: readonly string[] = [
  '🙏', '😊', '👍', '🙂', '😀', '😅', '🎉', '✨',
  '❤️', '🔥', '✅', '⭐', '📌', '📎', '📄', '📅',
  '🏠', '🏡', '🧭', '🕉️', '🪔', '🌸', '☀️', '🌙',
  '💬', '📞', '📧', '⏰', '👏', '🙌', '💯', '👌',
];

/** Popover max width in px (mirrors .emoji-pop max-width). */
const POP_MAX_W = 288;

export function EmojiPicker({
  onPick,
  ariaLabel = 'Insert emoji',
  disabled = false,
}: {
  onPick: (emoji: string) => void;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  /** Close and return focus to the trigger (keyboard-close contract). */
  function closeAndRefocus() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  // Decide alignment BEFORE paint so the popover never flashes off-screen.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    setAlignRight(rect.left + POP_MAX_W > vw - 8);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeAndRefocus();
    }
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        // Outside pointer-dismiss returns focus to the trigger too.
        closeAndRefocus();
      }
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <div className="emoji-wrap" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className="emoji-trigger"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={ariaLabel}
        title={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">😀</span>
      </button>
      {open && (
        <div
          className={`emoji-pop${alignRight ? ' align-right' : ''}`}
          role="group"
          aria-label="Emoji"
        >
          {EMOJI.map((e) => (
            <button
              key={e}
              type="button"
              className="emoji-cell"
              aria-label={`Insert ${e}`}
              onClick={() => {
                onPick(e);
                setOpen(false);
                triggerRef.current?.focus();
              }}
            >
              <span aria-hidden="true">{e}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
